/**
 * As 9 regras de construcao de distratores (secao 2.2 do catalogo CAEd),
 * transformadas de prosa em INVARIANTES VERIFICADAS EM CODIGO.
 *
 * Duas delas nao fazem sentido por item, so em agregado sobre uma varredura de
 * seeds ("a chave nao e sistematicamente o maior nem o menor"); essas ficam em
 * `aggregateKeyPosition`, chamada pela bateria de testes.
 */

import {
  isArithmeticProgression,
  isGeometricProgression,
  relativeSeparation,
} from '../core/num.ts';
import { toPlainText, visibleLength } from '../schema/richtext.ts';
import type { Alternative, SelectOneInteraction } from '../schema/item.ts';
import type { ChoiceId } from '../schema/taxonomy.ts';

export interface AlternativeInvariantReport {
  readonly keyIsUnique: boolean;
  readonly allDistinct: boolean;
  readonly everyDistractorAnchored: boolean;
  readonly sameDecimals: boolean;
  /** max/min do comprimento de texto; deve ficar abaixo de 1,6. */
  readonly textLengthRatio: number;
  readonly minRelSeparation: number;
  readonly isArithmeticProgression: boolean;
  readonly isGeometricProgression: boolean;
  readonly sortedAscending: boolean;
  readonly keyIsExtreme: boolean;
  readonly violations: readonly string[];
}

export function checkAlternatives(
  inter: SelectOneInteraction,
  opts: { minRelSep?: number; maxTextRatio?: number } = {},
): AlternativeInvariantReport {
  const minRelSep = opts.minRelSep ?? 0.02;
  const maxTextRatio = opts.maxTextRatio ?? 1.6;

  const alts = inter.alternatives;
  const violations: string[] = [];

  // --- chave unica e distratores ancorados ---------------------------------
  const keys = alts.filter((a) => a.error === null);
  const keyIsUnique = keys.length === 1 && keys[0]?.id === inter.keyId;
  if (!keyIsUnique) violations.push('a chave nao e unica ou nao corresponde a keyId');

  const everyDistractorAnchored = alts.every((a) => a.id === inter.keyId || a.error !== null);
  if (!everyDistractorAnchored) {
    violations.push('ha distrator sem erro nomeado (ancoragem em erro violada)');
  }

  // --- todas distintas ------------------------------------------------------
  const raws = alts.map((a) => a.rawValue);
  const nums = raws.filter((r): r is number => typeof r === 'number');
  const allNumeric = nums.length === alts.length;

  const chaves = new Set(alts.map((a) => keyOfAlt(a)));
  const allDistinct = chaves.size === alts.length;
  if (!allDistinct) violations.push('ha alternativas repetidas');

  // --- homogeneidade formal -------------------------------------------------
  // A homogeneidade e sobre a APARENCIA, nao sobre o valor: "24" e "24,25" exibidos
  // como "24,00" e "24,25" sao homogeneos, ainda que os numeros tenham precisoes
  // intrinsecas diferentes. Por isso a contagem sai do texto renderizado.
  let sameDecimals = true;
  if (allNumeric) {
    const dps = new Set(alts.map((a) => displayedDecimals(visibleText(a))));
    sameDecimals = dps.size === 1;
    if (!sameDecimals) violations.push('as alternativas numericas usam casas decimais diferentes');
  }

  const comprimentos = alts.map((a) => Math.max(visibleLength(a.content), 1));
  const textLengthRatio = Math.max(...comprimentos) / Math.min(...comprimentos);
  if (textLengthRatio > maxTextRatio) {
    violations.push(`extensao de texto desigual (razao ${textLengthRatio.toFixed(2)})`);
  }

  // --- proximidade controlada ----------------------------------------------
  let minRelSeparation = Infinity;
  if (allNumeric) {
    for (let i = 0; i < nums.length; i++) {
      for (let j = i + 1; j < nums.length; j++) {
        minRelSeparation = Math.min(
          minRelSeparation,
          relativeSeparation(nums[i] as number, nums[j] as number),
        );
      }
    }
    if (minRelSeparation < minRelSep) {
      violations.push(
        `duas alternativas quase identicas (separacao ${minRelSeparation.toFixed(4)})`,
      );
    }
  } else {
    minRelSeparation = Infinity;
  }

  // --- sem progressao obvia -------------------------------------------------
  const ordenados = allNumeric ? [...nums].sort((a, b) => a - b) : [];
  const pa = allNumeric && isArithmeticProgression(ordenados);
  const pg = allNumeric && isGeometricProgression(ordenados);
  if (pa) violations.push('as alternativas formam uma progressao aritmetica perfeita');
  if (pg) violations.push('as alternativas formam uma progressao geometrica perfeita');

  // --- ordenacao ------------------------------------------------------------
  let sortedAscending = true;
  if (allNumeric) {
    sortedAscending = nums.every((n, i) => i === 0 || n >= (nums[i - 1] as number));
    if (!sortedAscending) violations.push('as alternativas numericas nao estao em ordem crescente');
  }

  // --- posicao da chave (por item so registramos; a regra e agregada) -------
  let keyIsExtreme = false;
  if (allNumeric) {
    const keyAlt = alts.find((a) => a.id === inter.keyId);
    const kv = keyAlt?.rawValue;
    if (typeof kv === 'number') {
      keyIsExtreme = kv === Math.min(...nums) || kv === Math.max(...nums);
    }
  }

  return {
    keyIsUnique,
    allDistinct,
    everyDistractorAnchored,
    sameDecimals,
    textLengthRatio,
    minRelSeparation,
    isArithmeticProgression: pa,
    isGeometricProgression: pg,
    sortedAscending,
    keyIsExtreme,
    violations,
  };
}

function visibleText(a: Alternative): string {
  return toPlainText(a.content);
}

/**
 * Casas decimais EXIBIDAS no ultimo numero do texto.
 *
 * Em pt-BR a virgula separa os decimais e o PONTO separa os milhares: "1.260" nao tem
 * tres casas decimais, e le-se como mil duzentos e sessenta. Uma versao anterior
 * aceitava os dois como separador decimal e rejeitava toda variante em que "360°"
 * aparecia ao lado de "1.260°".
 */
function displayedDecimals(s: string): number {
  const matches = [...s.matchAll(/\d+(?:\.\d{3})*(?:,(\d+))?/g)];
  const last = matches[matches.length - 1];
  return last?.[1]?.length ?? 0;
}

function keyOfAlt(a: Alternative): string {
  return a.rawValue !== null
    ? `v:${String(a.rawValue)}`
    : `t:${visibleLength(a.content)}:${JSON.stringify(a.content)}`;
}

// ---------------------------------------------------------------------------
// Regras agregadas -- so fazem sentido sobre uma varredura de seeds
// ---------------------------------------------------------------------------

export interface KeyPositionReport {
  readonly n: number;
  readonly counts: Readonly<Record<ChoiceId, number>>;
  /** Fracao de itens em que a chave e o menor ou o maior valor. */
  readonly extremeRate: number;
  /** Qui-quadrado da distribuicao de posicoes contra a uniforme. */
  readonly chiSquare: number;
  /** Graus de liberdade = 4. Valor critico a 1% = 13,28. */
  readonly uniformOk: boolean;
}

export function aggregateKeyPosition(
  observations: readonly { keyId: ChoiceId; keyIsExtreme: boolean }[],
): KeyPositionReport {
  const counts: Record<ChoiceId, number> = { A: 0, B: 0, C: 0, D: 0, E: 0 };
  let extremos = 0;

  for (const o of observations) {
    counts[o.keyId]++;
    if (o.keyIsExtreme) extremos++;
  }

  const n = observations.length;
  const esperado = n / 5;
  const chiSquare =
    esperado > 0
      ? (Object.values(counts) as number[]).reduce(
          (s, c) => s + ((c - esperado) * (c - esperado)) / esperado,
          0,
        )
      : 0;

  return {
    n,
    counts,
    extremeRate: n > 0 ? extremos / n : 0,
    chiSquare,
    uniformOk: chiSquare < 13.28,
  };
}
